import type { ListLateCancelsParams } from '@shared/api/late-cancels.service'
import type { LateCancelCountsDto, LateCancelListResponseDto } from '@shared/core/cancellation-policy'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { ApiService } from 'src/services/api.service'
import { GET_LATE_CANCEL_COUNTS_CACHE_KEY } from '../constants/cancellation-policy-cache-keys'

export { GET_LATE_CANCEL_COUNTS_CACHE_KEY }
export const GET_LATE_CANCELS_CACHE_KEY = 'get-late-cancels'

export function useLateCancels(params: ListLateCancelsParams, options?: UseQueryOptions<LateCancelListResponseDto>) {
  const handleRetry = useHandleRetry()

  return useQuery([GET_LATE_CANCELS_CACHE_KEY, params.status, params.page], () => ApiService.LateCancels.list(params), {
    retry: handleRetry,
    keepPreviousData: true,
    ...options,
  })
}

export function useLateCancelCounts(options?: UseQueryOptions<LateCancelCountsDto>) {
  const handleRetry = useHandleRetry()

  return useQuery([GET_LATE_CANCEL_COUNTS_CACHE_KEY], () => ApiService.LateCancels.counts(), {
    retry: handleRetry,
    ...options,
  })
}

function useInvalidateLateCancels() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries([GET_LATE_CANCELS_CACHE_KEY])
    queryClient.invalidateQueries([GET_LATE_CANCEL_COUNTS_CACHE_KEY])
  }
}

export function useResolveLateCancel() {
  const invalidate = useInvalidateLateCancels()
  return useMutation((id: string) => ApiService.LateCancels.resolve(id), { onSuccess: invalidate })
}

export function useUnresolveLateCancel() {
  const invalidate = useInvalidateLateCancels()
  return useMutation((id: string) => ApiService.LateCancels.unresolve(id), { onSuccess: invalidate })
}
